import type { AcknowledgementLogPage, TaskLog, TaskLogQuery, TaskPage, TaskPinBoardPage } from "@storyboard/web-client";
import { ApiError, listAcknowledgementLogPage, listTaskPinBoards, listTasks } from "@storyboard/web-client";
import { transport } from "@/config";
import { upsertAcknowledgements } from "@/models/acknowledgement/acknowledgementStore";
import { registryStateStore, type SyncStateStore } from "@/models/sync/cursorStore";
import { browserPollerClock, createSingleFlight, startPolling } from "@/models/sync/eventLogSync";
import type { SyncContext } from "@/models/sync/syncTargets";
import { upsertTaskPinBoards, upsertTasks } from "@/models/task/taskStore";
import { nextTmsEventPollDelayMs } from "@/models/tmsEvent/tmsEventPoller";
import { recordEvent } from "@/services/telemetry";

/** Rows the board asks for per page, the most the routes serve. */
export const TASK_PAGE_SIZE = 500;

/**
 * Both task logs, the ones a task hint names.
 *
 * Written as a record the compiler checks against the generated union, so a log the API adds fails to compile here.
 */
export const TASK_LOGS: readonly TaskLog[] = Object.keys({ task: true, pin_board: true } satisfies Record<
  TaskLog,
  true
>) as TaskLog[];

/**
 * Every log the task sync walks: the two task logs, and the task acknowledgements' cursor-paged log.
 *
 * Wider than `TaskLog`, the task hint's vocabulary: a task acknowledgement hints as the acknowledgement log's `task`
 * subject, never as a task log.
 */
export type TaskSyncLog = TaskLog | "acknowledgement";

/** Every log a task sync walks, in the order a sync walks them. */
export const TASK_SYNC_LOGS: readonly TaskSyncLog[] = [...TASK_LOGS, "acknowledgement"];

/** Registry key of the cursor a log's last walk ended on. */
export function taskCursorKey(log: TaskSyncLog): string {
  return `taskCursor:${log}`;
}

/** One page of any of the logs. */
export type TaskLogPage = TaskPage | TaskPinBoardPage | AcknowledgementLogPage;

/** The collaborators the sync reads and writes through; production binds the transport, the registry and the store. */
export type TaskSyncDeps = {
  fetch: (log: TaskSyncLog, query: TaskLogQuery) => Promise<TaskLogPage>;
  cursors: SyncStateStore;
  apply: (log: TaskSyncLog, page: TaskLogPage) => void;
  report: (name: string, context: Record<string, unknown>) => void;
};

/**
 * Binds the task reads to the store: per log, a walk of pages, each landing as it arrives.
 *
 * A cold start sends no cursor and no `since`, and the server floors the walk at its lookback; every later request
 * sends back the cursor the previous answer carried. While an answer's `hasMore` is true, the next page is requested at
 * once with that answer's `cursor`, held in memory; the answer that ends the walk persists its `cursor` for the next
 * tick. A refused cursor (400) is dropped, so the next sync cold-starts. Each `(workspace, log)` walk is single-flight:
 * a tick, a hint, or a boot landing during one shares it.
 */
export function createTaskSync(deps: TaskSyncDeps): { walk: (ctx: SyncContext, log: TaskSyncLog) => Promise<void> } {
  const flights = createSingleFlight();
  const openCursors = new Map<string, string>();

  /**
   * The persisted walk-ending cursor, or none.
   *
   * An unreadable registry reads as nothing held, so it costs one cold start, and is reported to the reliability log.
   */
  async function persistedCursor(workspaceId: string, log: TaskSyncLog): Promise<string | undefined> {
    try {
      return await deps.cursors.get(taskCursorKey(log), workspaceId);
    } catch (error) {
      deps.report("tasks.cursor_read_failed", {
        tasks: { workspaceId, log, error: error instanceof Error ? error.message : String(error) },
      });
      return undefined;
    }
  }

  async function walk({ workspaceId }: SyncContext, log: TaskSyncLog, flight: string): Promise<void> {
    let cursor = openCursors.get(flight) ?? (await persistedCursor(workspaceId, log));

    for (;;) {
      // A failed page leaves the persisted cursor as it was; the next sync re-sends the same cursor, and a page applied
      // twice is harmless.
      let page: TaskLogPage;
      try {
        page = await deps.fetch(log, { workspaceId, cursor, limit: TASK_PAGE_SIZE });
      } catch (error) {
        const status = error instanceof ApiError ? error.status : undefined;
        // The server refused the cursor: drop it, so the next sync cold-starts.
        if (status === 400) {
          openCursors.delete(flight);
          await deps.cursors.set(taskCursorKey(log), workspaceId, "");
        }
        deps.report("tasks.refresh_failed", {
          tasks: { workspaceId, log, status, error: error instanceof Error ? error.message : String(error) },
        });
        return;
      }

      deps.apply(log, page);

      // More pages wait: hold the cursor in memory and ask for the next one now.
      if (page.hasMore) {
        openCursors.set(flight, page.cursor);
        cursor = page.cursor;
        continue;
      }

      // Rows land before the cursor that covers them, so an interrupted walk re-reads rather than skips.
      await deps.cursors.set(taskCursorKey(log), workspaceId, page.cursor);
      openCursors.delete(flight);
      return;
    }
  }

  return {
    walk: (ctx, log) => {
      const flight = `${ctx.workspaceId}:${log}`;
      return flights.join(flight, () => walk(ctx, log, flight));
    },
  };
}

/** Lands a page in the collection its log fills. */
function applyTaskPage(_log: TaskSyncLog, page: TaskLogPage): void {
  if ("tasks" in page) upsertTasks(page.tasks);
  else if ("pinBoards" in page) upsertTaskPinBoards(page.pinBoards);
  else upsertAcknowledgements(page.acknowledgements);
}

/** Reads one page of a log from its route. */
function fetchTaskPage(log: TaskSyncLog, query: TaskLogQuery): Promise<TaskLogPage> {
  switch (log) {
    case "task":
      return listTasks(transport, query);
    case "pin_board":
      return listTaskPinBoards(transport, query);
    case "acknowledgement":
      return listAcknowledgementLogPage(transport, { ...query, subject: "task" });
  }
}

const report = (name: string, context: Record<string, unknown>) => recordEvent(name, context, "reliability");

const production = createTaskSync({
  fetch: fetchTaskPage,
  cursors: registryStateStore,
  apply: applyTaskPage,
  report,
});

/** Brings one log up to date: a boot, a tick, a hint, or a resync. */
export function walkTaskLog(ctx: SyncContext, log: TaskSyncLog): Promise<void> {
  return production.walk(ctx, log);
}

/** Walks every log at once; a log that fails is reported and left to its next poll, and never stops the others. */
export async function walkAllTasks(ctx: SyncContext): Promise<void> {
  await Promise.all(
    TASK_SYNC_LOGS.map(async (log) => {
      try {
        await walkTaskLog(ctx, log);
      } catch (error) {
        report("tasks.refresh_failed", {
          tasks: { workspaceId: ctx.workspaceId, log, error: error instanceof Error ? error.message : String(error) },
        });
      }
    }),
  );
}

/**
 * Walks every log now and then on the TMS events' cadence for as long as the workspace is open.
 *
 * The poll is the floor under the hint, so a colleague's task or its acknowledgement is never more than about a minute
 * late. A hidden tab lets its ticks pass and walks once when shown again.
 *
 * @returns `stop` ends the poller, safe to call more than once; `bootstrapped` resolves once every log's first walk
 *   ends, and never rejects, so a boot can hold on it.
 */
export function startTaskSyncInBrowser(ctx: SyncContext): { stop: () => void; bootstrapped: Promise<void> } {
  const bootstrapped = walkAllTasks(ctx);
  const stop = startPolling(() => walkAllTasks(ctx), browserPollerClock(nextTmsEventPollDelayMs));
  return { stop, bootstrapped };
}
